import { ArrowUpRight, MapPin, Phone, Mail, RefreshCw, Globe2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Reveal, SectionTag, scrollToId, MaskedLine } from "./Reveal";
import { Logo } from "./Navbar";
import { presetRequest } from "./QuoteSection";
import { useAuth, homeForRole } from "../auth/AuthContext";

const NAV = [
  { label: "Solutions", id: "solutions" },
  { label: "Products", id: "products" },
  { label: "Managed Supply", id: "managed-supply" },
  { label: "How It Works", id: "how-it-works" },
  { label: "Supplier Network", id: "supplier-network" },
  { label: "About", id: "about" },
];

export default function ContactSection() {
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <section id="contact" data-testid="contact-section" className="relative pt-24 md:pt-32 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_100%,rgba(217,164,65,0.09),transparent_60%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <SectionTag number="17" label="Contact" />
        </Reveal>
        <h2 className="font-outfit font-extralight tracking-tighter leading-[0.98] text-4xl sm:text-6xl lg:text-7xl text-white" data-testid="contact-headline">
          <MaskedLine delay={0.1}>Stop Managing Fuel Procurement</MaskedLine>
          <MaskedLine delay={0.25}>
            <span className="text-gold">the Hard Way.</span>
          </MaskedLine>
        </h2>
        <Reveal delay={0.2}>
          <p className="mt-8 text-white/60 text-base md:text-lg max-w-2xl leading-relaxed">
            Tell Ajemi what you need — whether it&rsquo;s one shipment, recurring supply or
            procurement across multiple locations.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid="contact-request-quote-btn"
              onClick={() => presetRequest({ type: "one_time" })}
              className="group inline-flex items-center gap-3 bg-gold text-ink font-outfit font-medium rounded-full pl-7 pr-2.5 py-2.5 text-base hover:bg-goldbright hover:shadow-[0_0_44px_rgba(217,164,65,0.5)] transition-all duration-300"
            >
              Request a Quote
              <span className="bg-ink/10 rounded-full p-2 group-hover:rotate-45 transition-transform duration-300">
                <ArrowUpRight className="w-4 h-4" />
              </span>
            </button>
            <button
              data-testid="contact-managed-supply-btn"
              onClick={() => presetRequest({ type: "recurring" })}
              className="inline-flex items-center gap-2 border border-gold/50 text-gold font-outfit rounded-full px-7 py-3.5 text-base hover:bg-gold hover:text-ink transition-colors duration-300"
            >
              <RefreshCw className="w-4 h-4" />
              Discuss Managed Supply
            </button>
            <button
              data-testid="contact-international-btn"
              onClick={() => scrollToId("international")}
              className="inline-flex items-center gap-2 border border-white/20 text-white/85 font-outfit rounded-full px-7 py-3.5 text-base hover:border-gold/60 hover:text-gold transition-colors duration-300"
            >
              <Globe2 className="w-4 h-4" />
              International Procurement
            </button>
          </div>
        </Reveal>

        <div className="mt-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            {
              icon: MapPin,
              label: "Nigeria — Head Office",
              value: "Plot 8885, Cadastral Zone E, Mabushi District, Abuja, Nigeria",
              href: null,
              testId: "contact-address",
            },
            {
              icon: Globe2,
              label: "Canada — Calgary",
              value: "1018 Bridlemeadows SW, Calgary, AB · +1 403-427-0019",
              href: "tel:+14034270019",
              testId: "contact-canada",
            },
            {
              icon: Phone,
              label: "Phone / WhatsApp",
              value: "+234 806 075 2250",
              href: "https://wa.me/2348060752250",
              extra: { value: "+234 915 198 1008", href: "tel:+2349151981008" },
              extra2: { value: "WhatsApp: +1 403-427-0019", href: "https://wa.me/14034270019" },
              testId: "contact-phone",
            },
            {
              icon: Mail,
              label: "Procurement Desk",
              value: "procurement@ajemienergy.com",
              href: "mailto:procurement@ajemienergy.com",
              testId: "contact-email",
            },
          ].map((c, i) => (
            <Reveal key={c.label} delay={0.08 * i}>
              <div className="glass-panel rounded-xl p-6 h-full">
                <c.icon className="w-5 h-5 text-gold" strokeWidth={1.5} />
                <p className="text-[11px] tracking-[0.3em] uppercase text-white/40 mt-4">{c.label}</p>
                {c.href ? (
                  <a href={c.href} data-testid={c.testId} className="block text-white/80 text-sm mt-2 hover:text-gold transition-colors leading-relaxed">
                    {c.value}
                  </a>
                ) : (
                  <p data-testid={c.testId} className="text-white/80 text-sm mt-2 leading-relaxed">{c.value}</p>
                )}
                {c.extra && (
                  <a href={c.extra.href} data-testid={`${c.testId}-2`} className="block text-white/80 text-sm mt-1 hover:text-gold transition-colors leading-relaxed">
                    {c.extra.value}
                  </a>
                )}
                {c.extra2 && (
                  <a href={c.extra2.href} target="_blank" rel="noopener noreferrer" data-testid={`${c.testId}-3`} className="block text-white/60 text-sm mt-1 hover:text-gold transition-colors leading-relaxed">
                    {c.extra2.value}
                  </a>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <footer className="mt-24 border-t border-white/8" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <Logo compact={false} />
            <p className="text-xs text-white/40 mt-3">Energy Procurement &amp; Supply</p>
            <p className="text-[11px] text-white/30 mt-1">Ajemi Oil &amp; Gas Ltd.</p>
            <p className="text-[11px] text-white/30 mt-0.5">Nigeria · Canada / International Presence</p>
          </div>
          <nav className="flex flex-wrap gap-x-7 gap-y-3 items-center">
            {NAV.map((l) => (
              <button
                key={l.id}
                data-testid={`footer-link-${l.id}`}
                onClick={() => scrollToId(l.id)}
                className="text-sm text-white/45 hover:text-gold transition-colors"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="footer-signin-btn"
              onClick={() => navigate(user ? homeForRole(user.role) : "/login")}
              className="text-sm text-gold/80 hover:text-gold transition-colors"
            >
              {user ? "Portal" : "Sign In"}
            </button>
          </nav>
        </div>
        <div className="border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex flex-wrap gap-x-7 gap-y-2 items-center" data-testid="footer-legal-links">
            <Link to="/legal#privacy" className="text-xs text-white/40 hover:text-gold transition-colors" data-testid="footer-link-privacy">Privacy Policy</Link>
            <Link to="/legal#terms" className="text-xs text-white/40 hover:text-gold transition-colors" data-testid="footer-link-terms">Terms of Service</Link>
            <Link to="/legal#supplier-terms" className="text-xs text-white/40 hover:text-gold transition-colors" data-testid="footer-link-supplier-terms">Supplier Terms</Link>
            <Link to="/legal#customer-terms" className="text-xs text-white/40 hover:text-gold transition-colors" data-testid="footer-link-customer-terms">Customer Terms</Link>
          </div>
        </div>
        <div className="border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-white/35">
            <span data-testid="footer-copyright">© {new Date().getFullYear()} Ajemi Oil &amp; Gas Ltd. All rights reserved. Ajemi Energy is the commercial brand of Ajemi Oil &amp; Gas Ltd.</span>
            <span className="font-outfit tracking-[0.25em] uppercase">Energy Today. Powering Tomorrow.</span>
          </div>
        </div>
      </footer>
    </section>
  );
}
